// Failure isolation helpers. A module that throws is logged and disabled; the rest keeps running.

const errors = [];
const degraded = new Set();

export function getErrors() {
  return errors;
}
export function getDegraded() {
  return [...degraded];
}

export function recordError(module, err, context = 'init') {
  const entry = { module, context, message: String(err && err.message ? err.message : err), stack: err && err.stack, time: Date.now() };
  errors.push(entry);
  if (errors.length > 200) errors.shift();
  try {
    console.error(`[${module}] ${context}:`, err);
  } catch {}
  return entry;
}

/**
 * Run a module initialiser. Returns its result, or null if it threw (module marked degraded).
 * @template T
 * @param {string} module
 * @param {() => T} initFn
 * @param {{required?: boolean}} [opts]
 * @returns {T|null}
 */
export function safeInit(module, initFn, opts = {}) {
  try {
    return initFn();
  } catch (err) {
    recordError(module, err, 'init');
    degraded.add(module);
    if (opts.required) throw err;
    return null;
  }
}

/** Same as safeInit for async initialisers. */
export async function safeInitAsync(module, initFn, opts = {}) {
  try {
    return await initFn();
  } catch (err) {
    recordError(module, err, 'init');
    degraded.add(module);
    if (opts.required) throw err;
    return null;
  }
}

/**
 * Wrap a per-frame callback. After the first throw the callback is disabled for the session.
 * @param {string} module
 * @param {(...args: any[]) => void} fn
 */
export function safeFrame(module, fn) {
  let disabled = false;
  return function wrapped(...args) {
    if (disabled) return;
    try {
      fn(...args);
    } catch (err) {
      disabled = true;
      degraded.add(module);
      recordError(module, err, 'frame');
    }
  };
}

/** Wrap an event handler: errors are recorded but the handler stays enabled. */
export function safeHandler(module, fn) {
  return function wrapped(...args) {
    try {
      return fn(...args);
    } catch (err) {
      recordError(module, err, 'handler');
      return undefined;
    }
  };
}
